import { isRejectedWithValue, type Middleware, type MiddlewareAPI } from "@reduxjs/toolkit";
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { captureMessage } from "@sentry/react";
import { Mutex } from "async-mutex";
import { AuthTokens } from "aws-amplify/auth";

import { BASE_URL } from "../../constants";
import { postEnterpriseLogin, postLogout } from "../../features/authentication/services";
import { awsSignOut, currentSession } from "../../lib/aws";
import { getLocalValueByKey } from "../../utils";
import { RootState } from "../store";

const mutex = new Mutex();

// 後端 token 過期的辨識條件（部分端點如 /assets/upload 於 token 過期時回 HTTP 500 而非 401，
// body 為 { error_code: 2000, message: "Token is expired" }）。
const TOKEN_EXPIRED_ERROR_CODE = 2000;
const TOKEN_EXPIRED_MESSAGE = "Token is expired";

// 判斷這個回應是否代表 access token 過期、需要走刷新流程。
// 為避免誤傷其他 endpoint，非 401 的情況收到最緊：
// 僅在「HTTP 500 + error_code 2000 + message 為 Token is expired」三者皆符合時才視為過期。
// 任何其他狀態碼 / error_code / message 的錯誤都不會觸發刷新，行為與修改前一致。
const isTokenExpiredError = (error?: FetchBaseQueryError): boolean => {
    if (!error) return false;
    if (error.status === 401) return true;
    if (error.status !== 500) return false;
    const data = error.data as { error_code?: number; message?: string } | undefined;
    return data?.error_code === TOKEN_EXPIRED_ERROR_CODE && data?.message === TOKEN_EXPIRED_MESSAGE;
};

const baseQuery = fetchBaseQuery({
    baseUrl: BASE_URL,
    credentials: "include",
    prepareHeaders: (headers, api) => {
        const { getState } = api;
        const isLogin = (getState() as RootState).auth.isLogin;
        const token = getLocalValueByKey("amaze_accessToken") || null;

        // 如果 token 跟 cookie 同時存在給後端，後端會優先判斷 cookie
        if (isLogin && token) {
            headers.set("Authorization", "Bearer " + token);
        }
        return headers;
    },
});

const baseQueryWithReAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    await mutex.waitForUnlock();

    let result = await baseQuery(args, api, extraOptions);

    // accessToken expired handling
    // 觸發條件：HTTP 401，或後端以 500 + error_code 2000 表達的 token 過期（見 isTokenExpiredError）
    if (isTokenExpiredError(result?.error)) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                const amazeRefreshToken = localStorage.getItem("amaze_refreshToken") || null;
                const isEnterpriseAccountLogin = !!amazeRefreshToken;

                if (isEnterpriseAccountLogin) {
                    const res = await baseQuery(
                        {
                            url: "/token_refresh",
                            method: "POST",
                            body: { refresh_token: amazeRefreshToken },
                        },
                        api,
                        extraOptions
                    );
                    const refreshResult = res?.data as { access_token: string; refresh_token: string };
                    if (refreshResult?.access_token && refreshResult?.refresh_token) {
                        api.dispatch(
                            postEnterpriseLogin({
                                accessToken: refreshResult?.access_token,
                                refreshToken: refreshResult?.refresh_token,
                            })
                        );
                        result = await baseQuery(args, api, extraOptions);
                    } else {
                        api.abort();
                        apiSlice.util.resetApiState();
                        api.dispatch(postLogout());
                    }
                } else {
                    const sessionRes: AuthTokens | null = await currentSession();
                    const awsAccessToken = sessionRes?.accessToken.toString();

                    if (awsAccessToken) {
                        await baseQuery(
                            {
                                url: "/signin",
                                method: "POST",
                                body: { access_token: awsAccessToken },
                            },
                            api,
                            extraOptions
                        );
                        result = await baseQuery(args, api, extraOptions);
                    } else {
                        api.abort();
                        apiSlice.util.resetApiState();
                        api.dispatch(postLogout());
                        await awsSignOut();
                    }
                }
            } finally {
                release();
            }
        } else {
            await mutex.waitForUnlock();
            result = await baseQuery(args, api, extraOptions);
        }
    }

    return result;
};

export const apiSlice = createApi({
    baseQuery: baseQueryWithReAuth,
    tagTypes: [
        "Auth",
        "Knowledge",
        "Project",
        "Character",
        "ProjectCharacter",
        "Game",
        "ProjectGame",
        "ProjectMember",
        "KnowledgeRecord",
        "Voice",
        "Bot",
        "AzureAudio",
        "LineAcc",
        "IsBindLine",
        "JobStatus",
        "CrawlSchedule",
        "DefaultPrompts",
        "HintTexts",
        "HintText",
        "FbAcc",
        "IsBindFb",
        "VoiceKeywords",
        "VoiceKeyword",
        "VoiceKeywordsJob",
        "ServiceList",
        "Contract",
        "KnowledgeExport",
        "ProjectData",
        "UsageData",
        "ChatHistory",
        "ScriptTest",
        "ScriptTestReport",
        "ApiKey",
        "ChatWidgetSetting",
        "HomophoneFix",
        "CharacterVoices",
        "SearchRecord",
        "chatRecord",
        "chatHistoryId",
        "LLMSettings",
        "kioskSetting",
        "ApiKeys",
        "MCPSetting",
        "PocAccounts",
        "ChatWidgetShareLink",
    ],
    endpoints: () => ({}),
});

export const rtkQueryErrorMiddleware: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action) && action?.payload?.originalStatus !== 401) {
        // eslint-disable-next-line no-console -- for debugging
        console.log("🐑 ~ fetch api error:", action?.payload?.data || "API ERROR");
        captureMessage(action?.payload?.data || "API ERROR");
    }

    return next(action);
};
